import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { paymentMethodLabel, paymentMethodUsesBank, type ExpenseRecord } from "@/lib/expenses/expensesStore";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import { Banknote, CalendarIcon, ChevronRight, CreditCard, Edit, Receipt, Trash2, Wallet } from "lucide-react";

export type ExpenseDisplayLabels = {
  subcategory: string;
  category: string;
};

type ExpenseItemCardProps = {
  expense: ExpenseRecord;
  labels: ExpenseDisplayLabels;
  variant: "grid" | "list";
  onEdit?: () => void;
  onDelete?: () => void;
};

function formatExpenseDate(raw: string): string {
  const parsed = parseISO(raw);
  if (!isValid(parsed)) return raw || "Date not set";
  return format(parsed, "d MMM yyyy");
}

function paymentDisplay(expense: ExpenseRecord): string {
  const method = expense.paymentMethodLabel || paymentMethodLabel(expense.paymentMethod);
  const extras = [
    expense.bankName || null,
    expense.chequeNo ? `Cheque ${expense.chequeNo}` : null,
    expense.referenceNo || null,
  ].filter(Boolean);
  return extras.length ? `${method} · ${extras.join(" · ")}` : method;
}

export function ExpenseItemCard({ expense, labels, variant, onEdit, onDelete }: ExpenseItemCardProps) {
  const amount = formatCurrency(Number.isFinite(expense.amount) ? expense.amount : 0);
  const date = formatExpenseDate(expense.date);
  const payment = paymentDisplay(expense);
  const description = (expense.description ?? "").trim();
  const paidTo = (expense.expensePartyName ?? "").trim();
  const PaymentIcon = paymentMethodUsesBank(expense.paymentMethod) ? CreditCard : Wallet;
  const category = labels.category.trim();
  const showCategory = category && category.toLowerCase() !== "uncategorized";

  const titleBlock = (
    <div className="min-w-0 flex-1">
      <h3
        className={cn(
          "font-semibold text-foreground leading-snug break-words flex flex-wrap items-center gap-x-1 gap-y-0.5",
          variant === "grid" ? "text-base font-bold tracking-tight" : "text-sm",
        )}
        title={showCategory ? `${labels.subcategory} → ${category}` : labels.subcategory}
      >
        <span className="min-w-0 break-words">{labels.subcategory}</span>
        {showCategory ? (
          <>
            <ChevronRight className="w-4 h-4 shrink-0 text-primary/80" aria-hidden />
            <span className="min-w-0 break-words font-medium text-muted-foreground">{category}</span>
          </>
        ) : null}
      </h3>
    </div>
  );

  if (variant === "list") {
    const metaParts = [date, payment, paidTo || null, description || null].filter(Boolean);

    return (
      <div className="glass-card p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
            <Receipt className="w-8 h-8 text-muted-foreground/40" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {titleBlock}
              <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                {amount}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 break-words line-clamp-2">
              {metaParts.join(" · ")}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {onEdit ? (
              <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={onEdit}>
                <Edit className="w-3 h-3" /> Edit
              </Button>
            ) : null}
            {onDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                onClick={onDelete}
                aria-label="Delete expense"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm h-full flex flex-col min-w-0">
      <div className="h-1 w-full bg-primary" aria-hidden />
      <div className="p-4 pt-5 h-full flex flex-col gap-4 min-w-0">
        <div className="space-y-4 min-h-0 flex-1 min-w-0">
          <div className="flex items-start gap-2.5 min-w-0">
            <Receipt className="w-5 h-5 shrink-0 text-primary mt-0.5" strokeWidth={2} aria-hidden />
            {titleBlock}
          </div>

          <div className="flex gap-2.5 items-start min-w-0">
            <Banknote className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Amount</div>
              <div className="font-semibold text-sm text-foreground">{amount}</div>
            </div>
          </div>

          <div className="border-t border-border pt-3 min-w-0 space-y-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5 min-w-0">
              <CalendarIcon className="w-3 h-3 shrink-0 opacity-70" />
              <span className="truncate">{date}</span>
            </div>
            <div className="flex items-start gap-1.5 min-w-0 pl-[1.125rem]">
              <PaymentIcon className="w-3 h-3 shrink-0 opacity-70 mt-0.5" />
              <span className="break-words whitespace-normal line-clamp-2 min-w-0">{payment}</span>
            </div>
            {paidTo ? (
              <div className="break-words line-clamp-1 min-w-0 pl-[1.125rem]" title={paidTo}>
                Paid to: {paidTo}
              </div>
            ) : null}
            {description ? (
              <p className="text-sm text-foreground break-words whitespace-normal line-clamp-3 pl-[1.125rem]" title={description}>
                {description}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-auto">
          {onEdit ? (
            <Button type="button" variant="outline" size="sm" className="shrink-0 gap-2 h-9 px-3 text-xs font-medium" onClick={onEdit}>
              <Edit className="w-3.5 h-3.5 shrink-0" />
              Edit
            </Button>
          ) : null}
          {onDelete ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 border-destructive/60 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
